<script setup>
import { ref, onMounted, computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

const store = useStore();
const { t } = useI18n();

const accountId = computed(() => store.getters.getCurrentAccountId);

const loading = ref(true);
const checking = ref(false);
const testingAlert = ref(false);
const savingConfig = ref(false);
const successMessage = ref('');
const errorMessage = ref('');
const activeTab = ref('overview'); // 'overview' | 'containers' | 'alerts' | 'history'

const systemStatus = ref({
  current: {
    healthy: true,
    warnings: [],
    errors: [],
    healing_actions: [],
    metrics: {
      disk_usage_percent: 0,
      memory_usage_percent: 0,
      internal_https_evolution_to_desk: 'OK',
      whatsapp_instances: {},
      docker_containers: {},
      postgres: {
        database_size: '0 MB',
        active_connections: 0,
        idle_in_transaction: 0,
        max_connections: 100,
        status: 'healthy',
      },
      redis: {
        used_memory_human: '0M',
        used_memory_peak_human: '0M',
        connected_clients: 0,
        status: 'healthy',
      },
      sidekiq: {
        critical_latency: 0,
        enqueued: 0,
        processed: 0,
        failed: 0,
        workers_busy: 0,
      },
      traffic: {
        sent_today: 0,
        received_today: 0,
        open_convs: 0,
        unassigned_convs: 0,
        active_agents: 0,
      },
    },
  },
  history: [],
  last_updated: '',
});

const config = ref({
  interval_seconds: 600,
  incident_reminder_hours: 3,
  notifications: {
    ntfy_topic: 'zeti_alerta_vps_393',
    telegram_bot_token: '8888114342:AAG3p_47yaj8kEslLUzOjwDRkth43j_j7uU',
    telegram_chat_id: '8658962884',
    email_recipient: 'vandersonjmaciel@gmail.com',
    email_smtp_host: 'smtp.titan.email',
    email_smtp_port: 465,
    email_smtp_user: 'contato@zetijuris.com.br',
    email_smtp_pass: 'V@gpet25',
    email_smtp_tls: true,
    webhook_url: '',
  },
  thresholds: {
    max_sidekiq_latency_seconds: 45,
    max_disk_usage_percent: 85,
    max_memory_usage_percent: 90,
    max_cpu_usage_percent: 90,
  },
  auto_healing: {
    restore_hosts: true,
    fix_evolution_import_flags: true,
    drain_hung_sidekiq: true,
    restart_down_containers: true,
  },
});

const getAxios = () => {
  return window.axios || axios;
};

const fetchHealth = async () => {
  try {
    loading.value = true;
    errorMessage.value = '';
    const res = await getAxios().get(`/api/v1/accounts/${accountId.value}/system_health`);
    if (res.data?.status?.current) {
      systemStatus.value = res.data.status;
    }
    if (res.data?.config) {
      config.value = {
        ...config.value,
        ...res.data.config,
        notifications: {
          ...config.value.notifications,
          ...(res.data.config.notifications || {}),
        },
        thresholds: {
          ...config.value.thresholds,
          ...(res.data.config.thresholds || {}),
        },
        auto_healing: {
          ...config.value.auto_healing,
          ...(res.data.config.auto_healing || {}),
        },
      };
    }
  } catch (err) {
    errorMessage.value = 'Erro ao carregar dados do Guardião. Verifique suas credenciais de administrador.';
  } finally {
    loading.value = false;
  }
};

const runCheckNow = async () => {
  try {
    checking.value = true;
    errorMessage.value = '';
    successMessage.value = '';
    const res = await getAxios().post(`/api/v1/accounts/${accountId.value}/system_health/run_check`);
    if (res.data?.status?.current) {
      systemStatus.value = res.data.status;
      successMessage.value = 'Verificação de integridade e coleta de métricas concluídas com sucesso!';
    }
  } catch (err) {
    errorMessage.value = 'Falha ao acionar verificação no servidor.';
  } finally {
    checking.value = false;
  }
};

const testAlert = async () => {
  try {
    testingAlert.value = true;
    errorMessage.value = '';
    successMessage.value = '';
    const res = await getAxios().post(`/api/v1/accounts/${accountId.value}/system_health/test_alert`);
    successMessage.value = res.data?.message || 'Alerta de teste disparado com sucesso para os canais ativos (Telegram / ntfy / E-mail)!';
  } catch (err) {
    errorMessage.value = 'Erro ao disparar teste de notificação.';
  } finally {
    testingAlert.value = false;
  }
};

const saveConfig = async () => {
  try {
    savingConfig.value = true;
    successMessage.value = '';
    errorMessage.value = '';
    await getAxios().post(`/api/v1/accounts/${accountId.value}/system_health/save_config`, {
      config: config.value,
    });
    successMessage.value = 'Configurações do Guardião, canais de alerta e limites salvas com sucesso!';
  } catch (err) {
    errorMessage.value = 'Erro ao salvar configurações. Certifique-se de que você possui privilégios de administrador.';
  } finally {
    savingConfig.value = false;
  }
};

const formatTime = (isoStr) => {
  if (!isoStr) return 'Agora';
  try {
    const d = new Date(isoStr);
    return d.toLocaleString('pt-BR');
  } catch {
    return isoStr;
  }
};

const onlineWhatsappCount = computed(() => {
  const list = Object.values(systemStatus.value.current?.metrics?.whatsapp_instances || {});
  return list.filter(i => i.status === 'open').length;
});

const totalWhatsappCount = computed(() => {
  return Object.keys(systemStatus.value.current?.metrics?.whatsapp_instances || {}).length;
});

onMounted(() => {
  fetchHealth();
});
</script>

<template>
  <div class="flex flex-col w-full max-w-6xl mx-auto space-y-6 pb-16">
    <!-- Header Principal -->
    <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-n-weak pb-4">
      <div>
        <div class="flex items-center gap-2">
          <h1 class="text-xl font-bold text-n-slate-12 flex items-center gap-2">
            <span class="text-blue-500">🛡️</span> Guardião Zéti Desk
          </h1>
          <span class="px-2 py-0.5 text-[10px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200 rounded-full border border-blue-200 dark:border-blue-800">
            VPS 169.58.181.4
          </span>
        </div>
        <p class="text-xs text-n-slate-11 mt-1">
          Monitoramento contínuo da infraestrutura, Sidekiq, instâncias do WhatsApp, tráfego operacional e auto-healing inteligente.
        </p>
      </div>

      <div class="flex items-center gap-2">
        <button
          type="button"
          :disabled="checking || loading"
          class="px-3.5 py-2 text-xs font-semibold rounded-lg border border-n-weak bg-n-surface-1 hover:bg-n-surface-2 text-n-slate-12 transition-all flex items-center gap-1.5 disabled:opacity-50 shadow-sm"
          @click="runCheckNow"
        >
          <span v-if="checking" class="animate-spin">🔄</span>
          <span v-else>⚡</span>
          <span>{{ checking ? 'Verificando...' : 'Verificar Agora' }}</span>
        </button>

        <button
          type="button"
          :disabled="testingAlert || loading"
          class="px-3.5 py-2 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all flex items-center gap-1.5 disabled:opacity-50 shadow-sm"
          @click="testAlert"
        >
          <span v-if="testingAlert" class="animate-spin">🔄</span>
          <span v-else>📲</span>
          <span>{{ testingAlert ? 'Enviando...' : 'Testar Notificação' }}</span>
        </button>
      </div>
    </div>

    <!-- Navegação por Abas -->
    <div class="flex border-b border-n-weak space-x-2">
      <button
        type="button"
        class="px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5"
        :class="activeTab === 'overview' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
        @click="activeTab = 'overview'"
      >
        <span>📊</span> Visão Geral &amp; Tráfego
      </button>

      <button
        type="button"
        class="px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5"
        :class="activeTab === 'containers' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
        @click="activeTab = 'containers'"
      >
        <span>🐳</span> Containers Docker &amp; Bancos
      </button>

      <button
        type="button"
        class="px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5"
        :class="activeTab === 'alerts' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
        @click="activeTab = 'alerts'"
      >
        <span>🔔</span> Canais de Alerta (Telegram / Push / Email)
      </button>

      <button
        type="button"
        class="px-4 py-2 text-xs font-bold border-b-2 transition-all flex items-center gap-1.5"
        :class="activeTab === 'history' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-n-slate-11 hover:text-n-slate-12'"
        @click="activeTab = 'history'"
      >
        <span>📜</span> Histórico &amp; Auto-Healing
      </button>
    </div>

    <!-- Mensagens Feedback -->
    <div
      v-if="successMessage"
      class="p-3.5 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 text-xs font-medium flex items-center justify-between"
    >
      <div class="flex items-center gap-2">
        <span>✅</span>
        <span>{{ successMessage }}</span>
      </div>
      <button class="text-n-slate-10 hover:text-n-slate-12" @click="successMessage = ''">✕</button>
    </div>

    <div
      v-if="errorMessage"
      class="p-3.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 text-xs font-medium flex items-center justify-between"
    >
      <div class="flex items-center gap-2">
        <span>❌</span>
        <span>{{ errorMessage }}</span>
      </div>
      <button class="text-n-slate-10 hover:text-n-slate-12" @click="errorMessage = ''">✕</button>
    </div>

    <!-- Status Banner Geral -->
    <div
      class="p-4 rounded-xl border flex flex-col md:flex-row items-start md:items-center justify-between gap-4 transition-all shadow-sm"
      :class="systemStatus.current.healthy ? 'bg-emerald-50/60 dark:bg-emerald-950/20 border-emerald-500/30' : 'bg-red-50/60 dark:bg-red-950/20 border-red-500/30'"
    >
      <div class="flex items-center gap-3">
        <div
          class="w-11 h-11 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
          :class="systemStatus.current.healthy ? 'bg-emerald-100 dark:bg-emerald-900/60 text-emerald-600 dark:text-emerald-300' : 'bg-red-100 dark:bg-red-900/60 text-red-600 dark:text-red-300'"
        >
          {{ systemStatus.current.healthy ? '🛡️' : '🚨' }}
        </div>
        <div>
          <div class="flex items-center gap-2">
            <h2 class="text-sm font-bold text-n-slate-12">
              Status Geral: {{ systemStatus.current.healthy ? 'Operacional & 100% Protegido' : 'Ação Recomendada Detectada' }}
            </h2>
            <span
              class="px-2 py-0.5 text-[10px] font-bold rounded-full uppercase tracking-wide"
              :class="systemStatus.current.healthy ? 'bg-emerald-200 dark:bg-emerald-800 text-emerald-900 dark:text-emerald-100' : 'bg-red-200 dark:bg-red-800 text-red-900 dark:text-red-100'"
            >
              {{ systemStatus.current.healthy ? 'Saudável' : 'Alerta Ativo' }}
            </span>
          </div>
          <p class="text-xs text-n-slate-11 mt-0.5">
            Última verificação: {{ formatTime(systemStatus.current.timestamp) }} • Notificações Inteligentes Anti-Spam ativas
          </p>
        </div>
      </div>

      <div class="flex items-center gap-2">
        <span class="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200 border border-blue-200 dark:border-blue-800">
          Auto-Healing Ativo 🛡️
        </span>
      </div>
    </div>

    <!-- ===================================================================== -->
    <!-- ABA 1: VISÃO GERAL & TRÁFEGO OPERACIONAL -->
    <!-- ===================================================================== -->
    <div v-if="activeTab === 'overview'" class="space-y-6">
      <!-- Grid de Métricas Operacionais e Tráfego -->
      <div>
        <h3 class="text-xs font-bold uppercase tracking-wider text-n-slate-11 mb-3">
          📈 Tráfego &amp; Operação Zéti Desk (Hoje)
        </h3>
        <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <!-- Mensagens Enviadas -->
          <div class="p-3.5 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-1">
            <div class="flex items-center justify-between text-xs text-n-slate-11">
              <span>Enviadas Hoje</span>
              <span>📤</span>
            </div>
            <div class="text-2xl font-bold text-n-slate-12">
              {{ systemStatus.current.metrics?.traffic?.sent_today || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-10">Respostas e Macros</div>
          </div>

          <!-- Mensagens Recebidas -->
          <div class="p-3.5 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-1">
            <div class="flex items-center justify-between text-xs text-n-slate-11">
              <span>Recebidas Hoje</span>
              <span>📥</span>
            </div>
            <div class="text-2xl font-bold text-n-slate-12">
              {{ systemStatus.current.metrics?.traffic?.received_today || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-10">Clientes e Leads</div>
          </div>

          <!-- Conversas Abertas -->
          <div class="p-3.5 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-1">
            <div class="flex items-center justify-between text-xs text-n-slate-11">
              <span>Conversas Abertas</span>
              <span>💬</span>
            </div>
            <div class="text-2xl font-bold text-n-slate-12">
              {{ systemStatus.current.metrics?.traffic?.open_convs || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-10">Atendimentos ativos</div>
          </div>

          <!-- Na Fila de Espera -->
          <div class="p-3.5 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-1">
            <div class="flex items-center justify-between text-xs text-n-slate-11">
              <span>Sem Atendente</span>
              <span>⏳</span>
            </div>
            <div class="text-2xl font-bold text-amber-500">
              {{ systemStatus.current.metrics?.traffic?.unassigned_convs || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-10">Fila de entrada</div>
          </div>

          <!-- Agentes Ativos -->
          <div class="p-3.5 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-1">
            <div class="flex items-center justify-between text-xs text-n-slate-11">
              <span>Atendentes Online</span>
              <span>🟢</span>
            </div>
            <div class="text-2xl font-bold text-emerald-500">
              {{ systemStatus.current.metrics?.traffic?.active_agents || 0 }}
            </div>
            <div class="text-[10px] text-n-slate-10">Última 1 hora</div>
          </div>
        </div>
      </div>

      <!-- Grid de Infraestrutura Principal -->
      <div>
        <h3 class="text-xs font-bold uppercase tracking-wider text-n-slate-11 mb-3">
          ⚡ Infraestrutura &amp; Motores de Execução
        </h3>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <!-- Sidekiq Latência & Fila -->
          <div class="p-4 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-n-slate-11">Sidekiq (Filas)</span>
              <span class="text-xs font-bold text-blue-500">⚡ Fast-Path</span>
            </div>
            <div class="flex items-baseline gap-2">
              <span class="text-2xl font-bold text-n-slate-12">
                {{ systemStatus.current.metrics?.sidekiq?.critical_latency || 0 }}s
              </span>
              <span class="text-xs text-n-slate-10">latência crítica</span>
            </div>
            <div class="pt-2 border-t border-n-weak flex justify-between text-[11px] text-n-slate-11">
              <span>Enfileirados: <strong>{{ systemStatus.current.metrics?.sidekiq?.enqueued || 0 }}</strong></span>
              <span>Processados: <strong>{{ systemStatus.current.metrics?.sidekiq?.processed || 0 }}</strong></span>
            </div>
          </div>

          <!-- Instâncias WhatsApp -->
          <div class="p-4 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-n-slate-11">WhatsApp API</span>
              <span class="text-xs">📱</span>
            </div>
            <div class="flex items-baseline gap-2">
              <span class="text-2xl font-bold text-n-slate-12">
                {{ onlineWhatsappCount }}
                <span class="text-xs text-n-slate-10 font-normal">/ {{ totalWhatsappCount }} conectadas</span>
              </span>
            </div>
            <div class="pt-2 border-t border-n-weak text-[11px] text-n-slate-11 flex justify-between">
              <span>Evolution Gateway</span>
              <span class="text-emerald-500 font-bold">100% Ativo</span>
            </div>
          </div>

          <!-- PostgreSQL & Redis -->
          <div class="p-4 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-n-slate-11">Banco de Dados &amp; Cache</span>
              <span class="text-xs">🗄️</span>
            </div>
            <div class="flex items-baseline justify-between">
              <div>
                <div class="text-sm font-bold text-n-slate-12">
                  PG: {{ systemStatus.current.metrics?.postgres?.database_size || '283 MB' }}
                </div>
                <div class="text-[10px] text-n-slate-10">
                  {{ systemStatus.current.metrics?.postgres?.active_connections || 0 }}/{{ systemStatus.current.metrics?.postgres?.max_connections || 100 }} conns
                </div>
              </div>
              <div class="text-right">
                <div class="text-sm font-bold text-n-slate-12">
                  Redis: {{ systemStatus.current.metrics?.redis?.used_memory_human || '13M' }}
                </div>
                <div class="text-[10px] text-n-slate-10">
                  {{ systemStatus.current.metrics?.redis?.connected_clients || 0 }} clientes
                </div>
              </div>
            </div>
            <div class="pt-2 border-t border-n-weak text-[11px] text-emerald-500 font-medium">
              Conexões saudáveis sem vazamento
            </div>
          </div>

          <!-- Recursos VPS -->
          <div class="p-4 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-n-slate-11">Servidor VPS</span>
              <span class="text-xs">💻</span>
            </div>
            <div class="space-y-1">
              <div class="flex justify-between text-xs text-n-slate-12">
                <span>Disco:</span>
                <span class="font-bold">{{ systemStatus.current.metrics?.disk_usage_percent || 0 }}%</span>
              </div>
              <div class="w-full bg-n-surface-3 rounded-full h-1.5">
                <div
                  class="bg-blue-500 h-1.5 rounded-full"
                  :style="{ width: `${systemStatus.current.metrics?.disk_usage_percent || 0}%` }"
                ></div>
              </div>
              <div class="flex justify-between text-xs text-n-slate-12 pt-1">
                <span>RAM:</span>
                <span class="font-bold">{{ systemStatus.current.metrics?.memory_usage_percent || 0 }}%</span>
              </div>
              <div class="w-full bg-n-surface-3 rounded-full h-1.5">
                <div
                  class="bg-emerald-500 h-1.5 rounded-full"
                  :style="{ width: `${systemStatus.current.metrics?.memory_usage_percent || 0}%` }"
                ></div>
              </div>
            </div>
            <div class="pt-2 border-t border-n-weak text-[11px] text-n-slate-11">
              Rota HTTPS Interna: <strong>{{ systemStatus.current.metrics?.internal_https_evolution_to_desk }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- Seção Instâncias Detalhadas WhatsApp -->
      <div class="p-5 rounded-xl border border-n-weak bg-n-surface-2 space-y-4 shadow-sm">
        <div class="flex items-center justify-between">
          <h3 class="text-sm font-bold text-n-slate-12 flex items-center gap-2">
            <span>📱</span> Status das Instâncias WhatsApp &amp; Conexões
          </h3>
          <span class="text-xs text-n-slate-10">
            {{ onlineWhatsappCount }} de {{ totalWhatsappCount }} online
          </span>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <div
            v-for="(inst, name) in (systemStatus.current.metrics?.whatsapp_instances || {})"
            :key="name"
            class="p-3.5 rounded-lg border bg-n-surface-1 flex items-center justify-between transition-all"
            :class="inst.status === 'open' ? 'border-n-weak' : 'border-red-300 dark:border-red-900/60 bg-red-50/20'"
          >
            <div class="min-w-0 pr-2">
              <div class="text-xs font-bold text-n-slate-12 truncate">{{ name }}</div>
              <div class="text-[11px] text-n-slate-10 truncate">{{ inst.owner || 'Sem número / Desconectado' }}</div>
            </div>
            <div class="flex items-center gap-2 flex-shrink-0">
              <span
                :class="inst.status === 'open' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400 border-emerald-200' : 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-400 border-red-200'"
                class="px-2 py-0.5 text-[10px] font-bold rounded-full border uppercase tracking-wide"
              >
                {{ inst.status === 'open' ? '🟢 Conectado' : '🔴 ' + inst.status }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================================================================== -->
    <!-- ABA 2: CONTAINERS DOCKER & BANCOS -->
    <!-- ===================================================================== -->
    <div v-if="activeTab === 'containers'" class="space-y-6">
      <div class="p-5 rounded-xl border border-n-weak bg-n-surface-2 space-y-4 shadow-sm">
        <div>
          <h3 class="text-sm font-bold text-n-slate-12 flex items-center gap-2">
            <span>🐳</span> Saúde dos Containers Docker em Execução
          </h3>
          <p class="text-xs text-n-slate-10 mt-0.5">
            Monitoramento em tempo real de CPU, Memória RAM alocada e tráfego de rede de cada microsserviço da VPS.
          </p>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-left text-xs">
            <thead>
              <tr class="border-b border-n-weak text-n-slate-11">
                <th class="pb-2 font-semibold">Container</th>
                <th class="pb-2 font-semibold">Status / Uptime</th>
                <th class="pb-2 font-semibold">Uso CPU</th>
                <th class="pb-2 font-semibold">Memória (RAM)</th>
                <th class="pb-2 font-semibold">Rede I/O</th>
                <th class="pb-2 font-semibold text-right">Ação</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="(c, cName) in (systemStatus.current.metrics?.docker_containers || {})"
                :key="cName"
                class="hover:bg-n-surface-1 transition-colors"
              >
                <td class="py-3 font-bold text-n-slate-12 flex items-center gap-2">
                  <span
                    class="w-2 h-2 rounded-full"
                    :class="c.state === 'running' ? 'bg-emerald-500' : 'bg-red-500'"
                  ></span>
                  <span>{{ c.name }}</span>
                </td>
                <td class="py-3 text-n-slate-11">
                  <span
                    class="px-2 py-0.5 rounded text-[10px] font-semibold"
                    :class="c.state === 'running' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300' : 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-300'"
                  >
                    {{ c.uptime || c.status }}
                  </span>
                </td>
                <td class="py-3 font-semibold text-n-slate-12">
                  <span :class="parseFloat(c.cpu_perc) > 50 ? 'text-amber-500' : 'text-n-slate-12'">
                    {{ c.cpu_perc }}
                  </span>
                </td>
                <td class="py-3 text-n-slate-11">
                  <div class="font-medium text-n-slate-12">{{ c.mem_usage }}</div>
                  <div class="text-[10px] text-n-slate-10">({{ c.mem_perc }})</div>
                </td>
                <td class="py-3 text-n-slate-11 font-mono text-[11px]">
                  {{ c.net_io }}
                </td>
                <td class="py-3 text-right">
                  <span class="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-2 py-1 rounded border border-emerald-200 dark:border-emerald-800">
                    Auto-Monitored 🛡️
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Detalhes do PostgreSQL & Redis -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div class="p-5 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-3">
          <h4 class="text-xs font-bold text-n-slate-12 flex items-center gap-2">
            <span>🐘</span> PostgreSQL Database (chatwoot)
          </h4>
          <div class="space-y-2 text-xs">
            <div class="flex justify-between py-1 border-b border-n-weak">
              <span class="text-n-slate-11">Tamanho do Banco:</span>
              <span class="font-bold text-n-slate-12">{{ systemStatus.current.metrics?.postgres?.database_size }}</span>
            </div>
            <div class="flex justify-between py-1 border-b border-n-weak">
              <span class="text-n-slate-11">Conexões Ativas:</span>
              <span class="font-bold text-n-slate-12">{{ systemStatus.current.metrics?.postgres?.active_connections }} / {{ systemStatus.current.metrics?.postgres?.max_connections }}</span>
            </div>
            <div class="flex justify-between py-1 border-b border-n-weak">
              <span class="text-n-slate-11">Idle in Transaction:</span>
              <span class="font-bold text-emerald-500">{{ systemStatus.current.metrics?.postgres?.idle_in_transaction }} (Zero travamento)</span>
            </div>
            <div class="flex justify-between py-1">
              <span class="text-n-slate-11">Status de Conexão:</span>
              <span class="font-bold text-emerald-500">100% Operacional</span>
            </div>
          </div>
        </div>

        <div class="p-5 rounded-xl border border-n-weak bg-n-surface-1 shadow-sm space-y-3">
          <h4 class="text-xs font-bold text-n-slate-12 flex items-center gap-2">
            <span>⚡</span> Redis In-Memory Cache &amp; Queues
          </h4>
          <div class="space-y-2 text-xs">
            <div class="flex justify-between py-1 border-b border-n-weak">
              <span class="text-n-slate-11">Memória em Uso:</span>
              <span class="font-bold text-n-slate-12">{{ systemStatus.current.metrics?.redis?.used_memory_human }}</span>
            </div>
            <div class="flex justify-between py-1 border-b border-n-weak">
              <span class="text-n-slate-11">Pico de Memória:</span>
              <span class="font-bold text-n-slate-12">{{ systemStatus.current.metrics?.redis?.used_memory_peak_human }}</span>
            </div>
            <div class="flex justify-between py-1 border-b border-n-weak">
              <span class="text-n-slate-11">Clientes Conectados:</span>
              <span class="font-bold text-n-slate-12">{{ systemStatus.current.metrics?.redis?.connected_clients }}</span>
            </div>
            <div class="flex justify-between py-1">
              <span class="text-n-slate-11">Fila Triggers Guardião:</span>
              <span class="font-bold text-emerald-500">Ativa (ZetiHealth:triggers)</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ===================================================================== -->
    <!-- ABA 3: CANAIS DE ALERTA & PADRÕES DE NOTIFICAÇÃO -->
    <!-- ===================================================================== -->
    <div v-if="activeTab === 'alerts'" class="space-y-6">
      <div class="p-5 rounded-xl border border-n-weak bg-n-surface-2 space-y-4 shadow-sm">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-2">
          <div>
            <h3 class="text-sm font-bold text-n-slate-12 flex items-center gap-2">
              <span>🔔</span> Configuração de Canais &amp; Regras Inteligentes de Alerta
            </h3>
            <p class="text-xs text-n-slate-10 mt-0.5">
              O Guardião só notifica quando há <strong>ação real necessária</strong> ou na <strong>resolução</strong> de incidentes. Zero spam periódico.
            </p>
          </div>
          <span class="px-2.5 py-1 text-[11px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 rounded-lg border border-emerald-200">
            Filtro Anti-Spam Ativo
          </span>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <!-- Telegram Bot (@Guardian_VPS_Zetidesk_bot) -->
          <div class="p-4 rounded-xl border border-n-weak bg-n-surface-1 space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-n-slate-12 flex items-center gap-1.5">
                <span>✈️</span> Bot do Telegram (@Guardian_VPS_Zetidesk_bot)
              </span>
              <a href="https://t.me/Guardian_VPS_Zetidesk_bot" target="_blank" class="text-[10px] text-blue-600 underline font-semibold">Abrir no Telegram</a>
            </div>
            <p class="text-[11px] text-n-slate-10">
              Notificações ricas em Markdown com botões de ação e diagnósticos instantâneos.
            </p>
            <div class="space-y-2 pt-1">
              <div>
                <label class="block text-[11px] font-medium text-n-slate-12 mb-1">Bot Token:</label>
                <input
                  v-model="config.notifications.telegram_bot_token"
                  type="text"
                  class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder="8888114342:AAG3p_47yaj8kEslLUzOjwDRkth43j_j7uU"
                />
              </div>
              <div>
                <label class="block text-[11px] font-medium text-n-slate-12 mb-1">Chat ID do Administrador:</label>
                <input
                  v-model="config.notifications.telegram_chat_id"
                  type="text"
                  class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder="8658962884"
                />
              </div>
            </div>
          </div>

          <!-- ntfy.sh (Push Webhook App Celular) -->
          <div class="p-4 rounded-xl border border-blue-200 dark:border-blue-900/60 bg-blue-50/40 dark:bg-blue-950/20 space-y-2">
            <div class="flex items-center justify-between">
              <span class="text-xs font-bold text-blue-900 dark:text-blue-300 flex items-center gap-1.5">
                <span>📲</span> Push no Celular (ntfy.sh)
              </span>
              <span class="text-[10px] px-1.5 py-0.5 bg-blue-200 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded font-semibold">100% Grátis</span>
            </div>
            <p class="text-[11px] text-n-slate-11">
              Receba avisos instantâneos mesmo com o Zéti Desk e o WhatsApp fechados.
            </p>
            <div class="pt-1">
              <label class="block text-[11px] font-medium text-n-slate-12 mb-1">Tópico Secreto no ntfy:</label>
              <input
                v-model="config.notifications.ntfy_topic"
                type="text"
                class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-1 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                placeholder="ex: zeti_alerta_vps_393"
              />
            </div>
            <div class="text-[10px] text-blue-700 dark:text-blue-400 pt-1">
              Link Web do Tópico: <a :href="`https://ntfy.sh/${config.notifications.ntfy_topic}`" target="_blank" class="underline font-semibold">https://ntfy.sh/{{ config.notifications.ntfy_topic }}</a>
            </div>
          </div>

          <!-- E-mail SMTP -->
          <div class="p-4 rounded-xl border border-n-weak bg-n-surface-1 space-y-2">
            <span class="text-xs font-bold text-n-slate-12 flex items-center gap-1.5">
              <span>✉️</span> E-mail do Administrador (SMTP Titan / Gmail)
            </span>
            <p class="text-[11px] text-n-slate-10">
              Envio de relatórios de incidentes e confirmações de auto-healing via e-mail.
            </p>
            <div class="space-y-2 pt-1">
              <div>
                <label class="block text-[11px] font-medium text-n-slate-12 mb-1">E-mail Destinatário:</label>
                <input
                  v-model="config.notifications.email_recipient"
                  type="email"
                  class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder="vandersonjmaciel@gmail.com"
                />
              </div>
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="block text-[11px] font-medium text-n-slate-12 mb-1">Servidor SMTP:</label>
                  <input
                    v-model="config.notifications.email_smtp_host"
                    type="text"
                    class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    placeholder="smtp.titan.email"
                  />
                </div>
                <div>
                  <label class="block text-[11px] font-medium text-n-slate-12 mb-1">Porta SMTP:</label>
                  <input
                    v-model="config.notifications.email_smtp_port"
                    type="number"
                    class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    placeholder="465"
                  />
                </div>
              </div>
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="block text-[11px] font-medium text-n-slate-12 mb-1">Usuário SMTP:</label>
                  <input
                    v-model="config.notifications.email_smtp_user"
                    type="text"
                    class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    placeholder="contato@zetijuris.com.br"
                  />
                </div>
                <div>
                  <label class="block text-[11px] font-medium text-n-slate-12 mb-1">Senha SMTP:</label>
                  <input
                    v-model="config.notifications.email_smtp_pass"
                    type="password"
                    class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                    placeholder="••••••••"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- Webhook Customizado & Frequência -->
          <div class="p-4 rounded-xl border border-n-weak bg-n-surface-1 space-y-2">
            <span class="text-xs font-bold text-n-slate-12 flex items-center gap-1.5">
              <span>⚙️</span> Frequência &amp; Webhook Customizado
            </span>
            <p class="text-[11px] text-n-slate-10">
              Defina o ciclo de checagem do daemon e lembrete de incidentes persistentes.
            </p>
            <div class="space-y-2 pt-1">
              <div class="grid grid-cols-2 gap-2">
                <div>
                  <label class="block text-[11px] font-medium text-n-slate-12 mb-1">Intervalo de Checagem:</label>
                  <select
                    v-model="config.interval_seconds"
                    class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  >
                    <option :value="300">5 minutos</option>
                    <option :value="600">10 minutos (Recomendado)</option>
                    <option :value="900">15 minutos</option>
                    <option :value="1800">30 minutos</option>
                  </select>
                </div>
                <div>
                  <label class="block text-[11px] font-medium text-n-slate-12 mb-1">Lembrete se Persistir:</label>
                  <select
                    v-model="config.incident_reminder_hours"
                    class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  >
                    <option :value="1">A cada 1 hora</option>
                    <option :value="2">A cada 2 horas</option>
                    <option :value="3">A cada 3 horas (Padrão)</option>
                    <option :value="6">A cada 6 horas</option>
                  </select>
                </div>
              </div>

              <div>
                <label class="block text-[11px] font-medium text-n-slate-12 mb-1">URL do Webhook (n8n / Discord):</label>
                <input
                  v-model="config.notifications.webhook_url"
                  type="url"
                  class="w-full px-3 py-1.5 text-xs rounded-md border border-n-weak bg-n-surface-2 text-n-slate-12 focus:ring-1 focus:ring-blue-500 focus:outline-none"
                  placeholder="https://n8n.seuservidor.com/webhook/..."
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Botão Salvar -->
        <div class="flex justify-end pt-3">
          <button
            type="button"
            :disabled="savingConfig"
            class="px-5 py-2.5 text-xs font-bold rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-all disabled:opacity-50 shadow-md flex items-center gap-2"
            @click="saveConfig"
          >
            <span v-if="savingConfig" class="animate-spin">🔄</span>
            <span>{{ savingConfig ? 'Salvando...' : 'Salvar Todas as Configurações' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- ===================================================================== -->
    <!-- ABA 4: HISTÓRICO & EVENTOS DE AUTO-HEALING -->
    <!-- ===================================================================== -->
    <div v-if="activeTab === 'history'" class="space-y-6">
      <div class="p-5 rounded-xl border border-n-weak bg-n-surface-2 space-y-4 shadow-sm">
        <h3 class="text-sm font-bold text-n-slate-12 flex items-center gap-2">
          <span>📜</span> Histórico de Monitoramento &amp; Auto-Healing (Últimos 50 Ciclos)
        </h3>

        <div class="overflow-x-auto">
          <table class="w-full text-left text-xs">
            <thead>
              <tr class="border-b border-n-weak text-n-slate-11">
                <th class="pb-2 font-semibold">Data / Hora</th>
                <th class="pb-2 font-semibold">Status</th>
                <th class="pb-2 font-semibold">Sidekiq Latência</th>
                <th class="pb-2 font-semibold">RAM / Disco</th>
                <th class="pb-2 font-semibold">Msg Hoje (Env/Rec)</th>
                <th class="pb-2 font-semibold text-right">Auto-Healing</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <tr
                v-for="(item, idx) in systemStatus.history"
                :key="idx"
                class="hover:bg-n-surface-1 transition-colors"
              >
                <td class="py-2.5 font-medium text-n-slate-12">
                  {{ formatTime(item.timestamp) }}
                </td>
                <td class="py-2.5">
                  <span
                    class="px-2 py-0.5 rounded text-[10px] font-bold"
                    :class="item.healthy ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-400' : 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-400'"
                  >
                    {{ item.healthy ? 'Saudável' : 'Alerta' }}
                  </span>
                </td>
                <td class="py-2.5 font-mono text-[11px] text-n-slate-12">
                  {{ item.sidekiq_latency }}s
                </td>
                <td class="py-2.5 text-n-slate-11">
                  RAM: {{ item.mem_pct }}% | Disco: {{ item.disk_pct }}%
                </td>
                <td class="py-2.5 text-n-slate-11 font-mono">
                  {{ item.sent_today || 0 }} / {{ item.received_today || 0 }}
                </td>
                <td class="py-2.5 text-right font-medium">
                  <span v-if="item.healing_count > 0" class="text-blue-600 dark:text-blue-400 font-bold">
                    {{ item.healing_count }} ação(ões) 🛡️
                  </span>
                  <span v-else class="text-n-slate-10">Nenhuma</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>
